import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchCartItems, 
  selectCartState, 
  selectIsUserLoggedIn, 
  selectIsAdminLoggedIn,
  removeFromCart,
  addToCart,
  setCartItems,
  selectUser
} from '../redux/WithcookieSlice';
import { useTranslation } from '../redux/TranslationHelper';
import { PriceDisplay } from './common/PriceDisplay';
import SafeImage from './common/SafeImage';

const CartComponent = ({ componentProps }) => {
  const { t } = useTranslation();
  
  const { 
    className = "cart-component-wrapper padding-tb-40",
    title = t("장바구니"),
    emptyText = t("장바구니가 비어 있습니다."),
    shippingFee = 4000, // 기본 배송비
    showStock = false // 재고 표시 여부 옵션 추가
  } = componentProps || {};
  
  const dispatch = useDispatch();
  const { cartItems, loading, error } = useSelector(selectCartState);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  const [removeLoading, setRemoveLoading] = useState(false);
  
  // 로컬에서 수량을 관리하기 위한 상태
  const [localQuantities, setLocalQuantities] = useState({});
  
  const navigate = useNavigate();

  // 컴포넌트 마운트 시 장바구니 데이터 로드
  useEffect(() => {
    if (isUserLoggedIn || isAdminLoggedIn) {
      dispatch(fetchCartItems());
    }
  }, [dispatch, isUserLoggedIn, isAdminLoggedIn]);

  // 초기 수량 설정
  useEffect(() => {
    if (cartItems && Array.isArray(cartItems)) {
      const initialQuantities = {};
      cartItems.forEach(item => {
        initialQuantities[item.id] = item.count || 1;
      });
      setLocalQuantities(initialQuantities);
    }
  }, [cartItems]);

  

  // 장바구니 상품 삭제 처리
  const handleRemoveFromCart = async (cartItemId) => {
    if (!cartItemId) {
      alert(t('삭제할 상품 정보가 올바르지 않습니다.'));
      return;
    }
    
    try {
      setRemoveLoading(true);
      // 장바구니에서 상품 삭제
      await dispatch(removeFromCart(cartItemId)).unwrap();
      // 장바구니 목록 다시 불러오기
      await dispatch(fetchCartItems());
      alert(t('상품이 장바구니에서 삭제되었습니다.'));
    } catch (error) {
      console.error('장바구니 상품 삭제 중 오류:', error);
      
      // 오류가 인증 관련 문제인 경우
      if (error.code === 'AUTH_REQUIRED') {
        alert(t('로그인이 필요한 서비스입니다. 로그인 페이지로 이동합니다.'));
        window.location.href = '/login';
      } else {
        alert(t('장바구니 상품 삭제에 실패했습니다. 다시 시도해주세요.'));
      }
    } finally {
      setRemoveLoading(false);
    }
  };

  // 장바구니 비우기
  const handleClearCart = async () => {
    // 확인 다이얼로그
    if (!window.confirm(t('장바구니를 비우시겠습니까?'))) {
      return;
    }
    
    try {
      // 장바구니의 모든 상품에 대해 삭제 요청
      if (cartItems && cartItems.length > 0) {
        setRemoveLoading(true);
        
        // 개별 삭제가 아닌 일괄 삭제로 수정
        for (const item of cartItems) {
          await dispatch(removeFromCart(item.id)).unwrap();
        }
        
        // 삭제 완료 후 장바구니 목록 다시 불러오기 (한 번만)
        await dispatch(fetchCartItems());
        alert(t('장바구니가 비워졌습니다.'));
      }
    } catch (error) {
      console.error('장바구니 비우기 중 오류:', error);
      alert(t('장바구니 비우기에 실패했습니다. 다시 시도해주세요.'));
    } finally {
      setRemoveLoading(false);
    }
  };

  // 수량 증가 처리
  const handleIncreaseQuantity = (item) => {
    // 현재 수량 가져오기
    const currentCount = item.count || 1;
    const newQuantity = currentCount + 1;
    
    // 로딩 상태 설정
    setLocalQuantities(prev => ({
      ...prev,
      [item.id]: newQuantity
    }));
    
    // 로컬 장바구니 상태도 업데이트
    const updatedCartItems = cartItems.map(cartItem => {
      if (cartItem.id === item.id) {
        return {
          ...cartItem,
          count: newQuantity
        };
      }
      return cartItem;
    });
    
    // Redux 스토어 업데이트
    dispatch(setCartItems(updatedCartItems));
    
    // addToCart API 호출하여 수량 업데이트 (백그라운드에서 진행)
    dispatch(addToCart({ 
      productId: item.product.id,
      count: newQuantity
    })).catch(error => {
      console.error('수량 증가 중 오류:', error);
      
      // 실패 시 로컬 상태 롤백
      setLocalQuantities(prev => ({
        ...prev,
        [item.id]: currentCount
      }));
      
      // Redux 스토어도 롤백
      dispatch(setCartItems(cartItems));
      
      // 에러 알림
      if (error.code === 'AUTH_REQUIRED') {
        alert(t('로그인이 필요한 서비스입니다.'));
      } else {
        const errorMessage = error.message || t('수량 변경에 실패했습니다.');
        alert(t('수량 변경 실패') + ': ' + errorMessage);
      }
    });
  };
  
  // 수량 감소 처리
  const handleDecreaseQuantity = (item) => {
    // 현재 수량 가져오기
    const currentCount = item.count || 1;
    
    // 수량이 1보다 클 때만 감소
    if (currentCount <= 1) return;
    
    const newQuantity = currentCount - 1;
    
    // 로딩 상태 설정
    setLocalQuantities(prev => ({
      ...prev,
      [item.id]: newQuantity
    }));
    
    // 로컬 장바구니 상태도 업데이트
    const updatedCartItems = cartItems.map(cartItem => {
      if (cartItem.id === item.id) {
        return {
          ...cartItem,
          count: newQuantity
        };
      }
      return cartItem;
    });
    
    // Redux 스토어 업데이트
    dispatch(setCartItems(updatedCartItems));
    
    // addToCart API 호출하여 수량 업데이트 (백그라운드에서 진행)
    dispatch(addToCart({ 
      productId: item.product.id,
      count: newQuantity
    })).catch(error => {
      console.error('수량 감소 중 오류:', error);
      
      // 실패 시 로컬 상태 롤백
      setLocalQuantities(prev => ({
        ...prev,
        [item.id]: currentCount
      }));
      
      // Redux 스토어도 롤백
      dispatch(setCartItems(cartItems));
      
      // 에러 알림
      if (error.code === 'AUTH_REQUIRED') {
        alert(t('로그인이 필요한 서비스입니다.'));
      } else {
        const errorMessage = error.message || t('수량 변경에 실패했습니다.');
        alert(t('수량 변경 실패') + ': ' + errorMessage);
      }
    });
  };

  // 상품 가격 합계 계산
  const calculateTotalPrice = () => {
    if (!cartItems || !Array.isArray(cartItems) || cartItems.length === 0) {
      return 0;
    }
    
    return cartItems.reduce((sum, item) => {
      const price = item.product?.config?.discounted_price 
        ? Number(item.product.config.discounted_price) 
        : item.product?.config?.default_price 
          ? Number(item.product.config.default_price) 
          : 0;
      
      // 로컬 수량을 사용하여 합계 계산
      const quantity = localQuantities[item.id] || item.count || 1;
      return sum + (price * quantity);
    }, 0);
  };

  // 총 금액 계산 (상품 가격 + 배송비)
  const calculateGrandTotal = () => {
    const totalPrice = calculateTotalPrice();
    return totalPrice + shippingFee;
  };

  // 결제 페이지로 이동하는 함수
  const handleCheckout = () => {
    // 장바구니가 비어있는지 확인
    if (!cartItems || cartItems.length === 0) {
      alert(t('장바구니가 비어 있습니다. 상품을 추가해주세요.'));
      return;
    }
    
    // 결제 페이지로 이동
    navigate('/checkout');
  };

  // 로딩 상태 표시
  if (loading || removeLoading) {
    return (
      <div className={className}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="cart-loading">
                <p>{t('장바구니 정보를 불러오는 중입니다...')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 에러 표시
  if (error) {
    return (
      <div className={className}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="cart-error">
                <p>{t('장바구니 정보를 불러오는데 실패했습니다')}: {typeof error === 'object' ? error.message || JSON.stringify(error) : error}</p>
                <button 
                  className="retry-button"
                  onClick={() => dispatch(fetchCartItems())}
                >
                  {t('다시 시도')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 로그인 필요
  if (!isUserLoggedIn && !isAdminLoggedIn) {
    return (
      <div className={className}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="cart-login-required">
                <p>{t('장바구니를 확인하려면 로그인이 필요합니다.')}</p>
                <Link to="/login" className="login-button">{t('로그인')}</Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 장바구니가 비어있는 경우
  if (!cartItems || (Array.isArray(cartItems) && cartItems.length === 0)) {
    return (
      <div className={className}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="cart-empty">
                <h2>{title}</h2>
                <p>{emptyText}</p>
                <Link to="/shopping" className="continue-shopping-button">
                  {t('쇼핑 계속하기')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 장바구니에 상품이 있는 경우 - 새로운 디자인
  return (
    <div className={className}>
      <div className="container">
        <div className="row" style={{ margin:"0 auto" }}>
          <div className="col-12">
            <h2 className="cart-page-title">{title}</h2>
          </div>
        </div>
        <div className="row" style={{ margin:"0 auto" }}>
          {/* 장바구니 상품 목록 */}
          <div className="col-lg-8 col-md-12">
            <div className="cart-items-list">
              {cartItems.map((item) => {
                // 상품 정보와 가격 가져오기
                const product = item.product || {};
                const config = product.config || {};
                const discountedPrice = config.discounted_price ? Number(config.discounted_price) : 0;
                const defaultPrice = config.default_price ? Number(config.default_price) : 0;
                const price = discountedPrice || defaultPrice;
                // 수량은 로컬 상태에서 가져옴
                const count = localQuantities[item.id] || item.count || 1;
                
                return (
                  <div className="cart-item" key={item.id}>
                    <div className="cart-item-inner" style={{ display: 'flex', flexDirection: 'column', padding: '15px', border: '1px solid #eee', borderRadius: '8px', marginBottom: '15px' }}>
                      <div style={{ display: 'flex', marginBottom: '10px' }}>
                        <div className="cart-item-image" style={{ width: '80px', height: '80px', marginRight: '15px' }}>
                          <Link to={`/products/${product.id}`}>
                            <SafeImage
                              src={config.img_url}
                              alt={product.title}
                              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                              placeholderStyle={{ width: '100%', height: '100%', background: '#cccccc', borderRadius: '4px' }}
                            />
                          </Link>
                        </div>
                        <div className="cart-item-details" style={{ flex: 1 }}>
                          <div className="cart-item-title" style={{ fontWeight: 'bold', marginBottom: '5px', fontSize: '16px' }}>
                            <Link to={`/products/${product.id}`} style={{ color: '#333', textDecoration: 'none' }}>
                              {t(product.title) || t('상품명 없음')}
                            </Link>
                          </div>
                          <div className="cart-item-price" style={{ color: '#666', marginBottom: '5px' }}>
                            <PriceDisplay price={price} />
                          </div>
                          {showStock && config.stock_count !== undefined && (
                            <div className="cart-item-stock" style={{ color: '#666', fontSize: '12px', marginBottom: '5px' }}>
                              {t('재고')}: {config.stock_count}{t('개')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="cart-item-actions" style={{ 
                        display: 'flex', 
                        flexDirection: 'row', 
                        alignItems: 'center', 
                        justifyContent: 'space-between', 
                        width: '100%', 
                        borderTop: '1px solid #eee',
                        paddingTop: '10px'
                      }}>
                        <div className="cart-item-quantity" style={{ width: 'auto', display: 'flex', alignItems: 'center' }}>
                          <div className="cart-quantity-control" style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            border: '1px solid #ddd', 
                            borderRadius: '4px', 
                            overflow: 'hidden'
                          }}>
                            <button 
                              className="quantity-decrease" 
                              onClick={() => handleDecreaseQuantity(item)}
                              disabled={count <= 1}
                              style={{ 
                                width: '30px', 
                                height: '30px', 
                                background: 'none', 
                                border: 'none', 
                                fontSize: '18px',
                                cursor: 'pointer'
                              }}
                            >-</button>
                            <span style={{ 
                              margin: '0 10px', 
                              minWidth: '20px', 
                              textAlign: 'center'
                            }}>{localQuantities[item.id] || count}</span>
                            <button 
                              className="quantity-increase" 
                              onClick={() => handleIncreaseQuantity(item)}
                              style={{ 
                                width: '30px', 
                                height: '30px', 
                                background: 'none', 
                                border: 'none', 
                                fontSize: '18px',
                                cursor: 'pointer'
                              }}
                            >+</button>
                          </div>
                        </div>
                        <div className="cart-item-total" style={{ 
                          fontWeight: 'bold', 
                          fontSize: '16px', 
                          color: '#333', 
                          minWidth: '100px', 
                          textAlign: 'right'
                        }}>
                          <PriceDisplay price={price * count} />
                        </div>
                        <div className="cart-item-remove">
                          <button 
                            onClick={() => handleRemoveFromCart(item.id)}
                            disabled={removeLoading}
                            style={{ 
                              background: 'none', 
                              border: 'none', 
                              fontSize: '16px',
                              color: '#999',
                              cursor: 'pointer'
                            }}
                            aria-label={t('상품 삭제')}
                          >
                            <i className="fa fa-times" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* 장바구니 요약 및 결제 정보 */}
          <div className="col-lg-4 col-md-12">
            <div className="cart-summary">
              <div className="cart-summary-inner">
                <div className="summary-title">
                  <h3>{t('장바구니 금액')}</h3>
                </div>
                <div className="summary-item">
                  <div className="item-label">{t('상품 금액')}</div>
                  <div className="item-value"><PriceDisplay price={calculateTotalPrice()} /></div>
                </div>
                <div className="summary-item">
                  <div className="item-label">{t('배송비')}</div>
                  <div className="item-value"><PriceDisplay price={shippingFee} /></div>
                </div>
                <div className="summary-total">
                  <div className="total-label">{t('총 결제금액')}</div>
                  <div className="total-value"><PriceDisplay price={calculateGrandTotal()} /></div>
                </div>
                <div className="summary-action">
                  <button 
                    className="checkout-button"
                    onClick={handleCheckout}
                  >
                    {t('결제하기')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartComponent; 